
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Award, 
  Calendar, 
  ChevronRight, 
  ExternalLink, 
  Filter, 
  Info, 
  Search
} from 'lucide-react';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FundingOpportunity } from '@/types/gigs';

// Mock data for grant opportunities
const mockGrants: FundingOpportunity[] = [
  {
    id: '1',
    name: 'Emerging Artists Fund',
    organization: 'National Arts Foundation',
    type: 'grant',
    amount: { min: 5000, max: 10000 },
    deadline: '2023-12-15',
    eligibility: ['Early career artists', 'Less than 5 years professional experience', 'Any musical genre'],
    description: 'Supporting innovative early-career musicians in developing new works and building sustainable careers.',
    website: 'https://example.com/emerging-artists',
    relevanceScore: 92,
    matchReason: [
      'Your career stage aligns perfectly with requirements',
      'Your genre focus matches their funding priorities',
      'Your recent project themes align with their mission'
    ],
    applicationProcess: 'Two-stage application with initial letter of interest followed by full proposal if selected.',
    successRate: 18
  },
  {
    id: '2',
    name: 'Digital Innovation Grant',
    organization: 'Music Tech Alliance',
    type: 'grant',
    amount: 7500,
    deadline: '2023-11-30',
    eligibility: ['Musicians exploring tech integration', 'Any career stage', 'Must have digital component'],
    description: 'Funding for artists exploring innovative digital technologies in music creation, performance, or distribution.',
    website: 'https://example.com/digital-innovation',
    relevanceScore: 85,
    matchReason: [
      'Your digital presence is strong',
      'Your previous work with interactive elements aligns with their goals',
      'Your audience demographics match their target impact metrics'
    ],
    applicationProcess: 'Single application with portfolio review and project proposal.',
    successRate: 12
  },
  {
    id: '3',
    name: 'Community Impact Fellowship',
    organization: 'Regional Arts Council',
    type: 'residency',
    amount: { min: 10000, max: 15000 },
    deadline: '2024-01-20',
    eligibility: ['Artists with community engagement focus', 'Must include educational component', 'Local residents preferred'],
    description: 'Supporting artists who integrate community engagement and education into their creative practice.',
    website: 'https://example.com/community-impact',
    relevanceScore: 78,
    matchReason: [
      'Your previous community workshops align with their mission',
      'Your local audience engagement metrics are strong',
      'Your genre is underrepresented in their current fellows'
    ],
    applicationProcess: 'Application with community impact proposal and letters of support.',
    successRate: 15
  },
  {
    id: '4',
    name: 'Touring Development Fund',
    organization: 'Music Export Office',
    type: 'grant',
    amount: { min: 3000, max: 8000 },
    deadline: '2023-12-05',
    eligibility: ['Artists with confirmed tour dates', 'Minimum 5 scheduled performances', 'Priority for first major tours'],
    description: 'Supporting emerging artists in developing their first significant tours nationally or internationally.',
    website: 'https://example.com/touring-fund',
    relevanceScore: 88,
    matchReason: [
      'Your upcoming tour schedule meets their requirements',
      'Your audience growth metrics align with their goals',
      'Your press coverage matches their visibility criteria'
    ],
    applicationProcess: 'Application with detailed tour plan, budget, and marketing strategy.',
    successRate: 22
  },
  {
    id: '5',
    name: 'Album Production Grant',
    organization: 'Sonic Foundation',
    type: 'grant',
    amount: { min: 5000, max: 12000 },
    deadline: '2024-02-10',
    eligibility: ['Artists with completed demos', 'Need funding for final production/mixing/mastering', 'Any genre'],
    description: 'Supporting independent artists in completing high-quality album projects with professional production.',
    website: 'https://example.com/album-production',
    relevanceScore: 95,
    matchReason: [
      'Your project timeline perfectly aligns with their funding cycle',
      'Your demos demonstrate strong artistic merit matching their criteria',
      'Your production plan meets their professional standards'
    ],
    applicationProcess: 'Application with demos, production plan, and budget breakdown.',
    successRate: 14
  }
];

const AIGrantMatchmaker: React.FC = () => {
  const [activeTab, setActiveTab] = useState('matches');
  const [minAmount, setMinAmount] = useState(3000);
  const [grants, setGrants] = useState(mockGrants);
  const [selectedGrant, setSelectedGrant] = useState<FundingOpportunity | null>(null);
  
  // Function to format amount display
  const formatAmount = (amount: number | { min: number; max: number }) => {
    if (typeof amount === 'number') {
      return `$${amount.toLocaleString()}`;
    } else {
      return `$${amount.min.toLocaleString()} - $${amount.max.toLocaleString()}`;
    }
  };
  
  // Function to calculate days until deadline
  const getDaysUntil = (dateString: string) => {
    const deadline = new Date(dateString);
    const today = new Date();
    const diffTime = deadline.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
      <div className="md:col-span-1 space-y-4">
        <Card className="border shadow-sm">
          <CardHeader>
            <CardTitle>Find Grants</CardTitle>
            <CardDescription>Filter opportunities that match your profile</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Grant Amount</label>
                <span className="text-sm text-muted-foreground">${minAmount.toLocaleString()}+</span>
              </div>
              <Slider 
                defaultValue={[3000]} 
                max={20000} 
                step={500} 
                value={[minAmount]}
                onValueChange={(values) => setMinAmount(values[0])}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Grant Type</label>
              <Select defaultValue="all">
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="grant">Grants</SelectItem>
                  <SelectItem value="scholarship">Scholarships</SelectItem>
                  <SelectItem value="residency">Residencies</SelectItem>
                  <SelectItem value="competition">Competitions</SelectItem>
                  <SelectItem value="sponsorship">Sponsorships</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Deadline</label>
              <Select defaultValue="all">
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select deadline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Time</SelectItem>
                  <SelectItem value="30">Next 30 Days</SelectItem>
                  <SelectItem value="60">Next 60 Days</SelectItem>
                  <SelectItem value="90">Next 90 Days</SelectItem>
                  <SelectItem value="future">Future Opportunities</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Match Quality</label>
              <Select defaultValue="all">
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select match quality" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Matches</SelectItem>
                  <SelectItem value="excellent">Excellent (90%+)</SelectItem>
                  <SelectItem value="good">Good (75-89%)</SelectItem>
                  <SelectItem value="fair">Fair (50-74%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Separator className="my-4" />
            
            <div>
              <label className="text-sm font-medium">Keywords</label>
              <div className="flex mt-1">
                <Input placeholder="Search by keyword" className="rounded-r-none" />
                <Button variant="outline" size="icon" className="rounded-l-none">
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <Button className="w-full">
              <Filter className="h-4 w-4 mr-2" /> Apply Filters
            </Button>
          </CardContent>
        </Card>
        
        <Card className="border shadow-sm bg-muted/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">AI Profile Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Profile Completeness</span>
                  <span className="font-medium">87%</span>
                </div>
                <Progress value={87} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Grant Readiness</span>
                  <span className="font-medium">75%</span>
                </div>
                <Progress value={75} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Matching Data Points</span>
                  <span className="font-medium">23/30</span>
                </div>
                <Progress value={76.6} className="h-2" />
              </div>
            </div>
            
            <div className="mt-4 text-sm space-y-2">
              <p className="font-medium">Suggestions to improve matches:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Add more details about past performances</li>
                <li>Upload more examples of your music</li>
                <li>Complete your artistic statement</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="md:col-span-2">
        <Card className="border shadow-sm h-full">
          <CardHeader className="pb-0 border-b">
            <div className="flex justify-between items-center">
              <CardTitle>Grant Opportunities</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setActiveTab('matches')} className={activeTab === 'matches' ? 'bg-primary text-primary-foreground' : ''}>
                  Best Matches
                </Button>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('saved')} className={activeTab === 'saved' ? 'bg-primary text-primary-foreground' : ''}>
                  Saved
                </Button>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('applied')} className={activeTab === 'applied' ? 'bg-primary text-primary-foreground' : ''}>
                  Applied
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {selectedGrant ? (
              <div className="p-6">
                <Button variant="ghost" size="sm" className="mb-4" onClick={() => setSelectedGrant(null)}>
                  ← Back to list
                </Button>
                
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedGrant.name}</h2>
                    <p className="text-muted-foreground">{selectedGrant.organization}</p>
                  </div>
                  <Badge className="text-sm px-3 py-1 bg-green-100 text-green-800 hover:bg-green-200 border-green-200">
                    {selectedGrant.relevanceScore}% Match
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">Amount</div>
                    <div className="font-semibold">{formatAmount(selectedGrant.amount)}</div>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">Deadline</div>
                    <div className="font-semibold">
                      {new Date(selectedGrant.deadline).toLocaleDateString()} 
                      <span className="text-sm font-normal ml-2 text-muted-foreground">
                        ({getDaysUntil(selectedGrant.deadline)} days left)
                      </span>
                    </div>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">Type</div>
                    <div className="font-semibold capitalize">{selectedGrant.type}</div>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">Success Rate</div>
                    <div className="font-semibold">{selectedGrant.successRate}%</div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Description</h3>
                    <p className="text-muted-foreground">{selectedGrant.description}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Eligibility</h3>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                      {selectedGrant.eligibility.map((criterion, index) => (
                        <li key={index}>{criterion}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Why You Match</h3>
                    <ul className="space-y-2">
                      {selectedGrant.matchReason?.map((reason, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <div className="mt-0.5 text-green-500">✓</div>
                          <span className="text-muted-foreground">{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Application Process</h3>
                    <p className="text-muted-foreground">{selectedGrant.applicationProcess}</p>
                  </div>
                  
                  <div className="flex gap-4 pt-4">
                    <Button className="flex-1">
                      Apply Now
                    </Button>
                    <Button variant="outline" className="flex-1">
                      Save for Later
                    </Button>
                    {selectedGrant.website && (
                      <Button variant="ghost" asChild>
                        <a href={selectedGrant.website} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="divide-y">
                {grants.map((grant) => (
                  <div 
                    key={grant.id} 
                    className="p-4 hover:bg-muted/50 cursor-pointer flex items-start justify-between"
                    onClick={() => setSelectedGrant(grant)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="bg-primary/10 p-2 rounded-full">
                        <Award className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{grant.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {grant.relevanceScore}% Match
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{grant.organization}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> 
                            {getDaysUntil(grant.deadline)} days left
                          </span>
                          <span>{formatAmount(grant.amount)}</span>
                          <span className="capitalize">{grant.type}</span>
                        </div>
                      </div>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <Info className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="w-80 p-0">
                          <div className="p-3">
                            <h4 className="font-medium mb-1">Why we think you match:</h4>
                            <ul className="text-xs space-y-1">
                              {grant.matchReason?.slice(0, 2).map((reason, index) => (
                                <li key={index} className="flex gap-1">
                                  <span className="text-green-500">✓</span>
                                  <span>{reason}</span>
                                </li>
                              ))}
                              {grant.matchReason && grant.matchReason.length > 2 && (
                                <li className="text-primary">+ more reasons</li>
                              )}
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
          <CardFooter className="border-t justify-between py-3">
            <div className="text-sm text-muted-foreground">
              Showing {grants.length} opportunities
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled>
                Previous
              </Button>
              <Button variant="outline" size="sm">
                Next
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default AIGrantMatchmaker;
